/** Paramètres → Flux : e-mails, conversion portail, archive liste. */
import { useEffect, useState } from "react";
import { Button } from "../../components/Button";
import { CheckField } from "../../components/CheckField";
import { useCompany } from "../../company/CompanyProvider";
import { workflowFromCompany, type CompanyWorkflow } from "../../workflow/types";
import { isCompanyOwner } from "./owner";

export function WorkflowTab() {
  const { company, updateWorkflow } = useCompany();
  const [draft, setDraft] = useState<CompanyWorkflow | null>(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!company) return;
    setDraft(workflowFromCompany(company));
    setMsg("");
    setErr("");
  }, [company]);

  if (!company || !draft) {
    return <p className="text-sm" style={{ color: "var(--fx-muted)" }}>Aucune société.</p>;
  }

  const owner = isCompanyOwner(company);

  function set<K extends keyof CompanyWorkflow>(key: K, value: CompanyWorkflow[K]) {
    setDraft((cur) => (cur ? { ...cur, [key]: value } : cur));
  }

  async function save() {
    if (!draft) return;
    setBusy(true);
    setErr("");
    setMsg("");
    try {
      await updateWorkflow(company.id, draft);
      setMsg("Enregistré.");
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Erreur");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fx-co-card">
      <header className="fx-co-card__head">
        <h2>Flux de travail</h2>
        <p>Envoyer sur une pièce valide et maille. Ici : récurrentes, conversion, archive.</p>
        {!owner ? (
          <p className="fx-co-form__hint">Lecture seule — seul le propriétaire enregistre le flux.</p>
        ) : null}
      </header>
      <div className="fx-fmt">
        <div className="fx-fmt-row">
          <span>Factures</span>
          <div className="flex flex-col gap-2">
            <p className="fx-co-form__hint m-0">Envoyer sur la pièce : numérote, fige et maille. Pas d’étape Valider à part.</p>
            <CheckField
              label="Archiver automatiquement lorsqu’elles sont payées"
              checked={draft.wf_archive_paid}
              onChange={(next) => set("wf_archive_paid", next)}
            />
            <p className="fx-co-form__hint m-0">Dès qu’elle est soldée, elle sort de la liste Factures. Elle reste consultable (fiche, recherche, relevé).</p>
          </div>
        </div>
        <div className="fx-fmt-row">
          <span>Devis</span>
          <div className="flex flex-col gap-2">
            <p className="fx-co-form__hint m-0">Envoyer sur la pièce : numérote, fige et maille. Pas d’étape Valider à part.</p>
            <CheckField
              label="Convertir automatiquement en facture dès que le client approuve"
              checked={draft.wf_quote_convert}
              onChange={(next) => set("wf_quote_convert", next)}
            />
            <p className="fx-co-form__hint m-0">Signature portail : brouillon facture seulement si la case est cochée. Convertir reste possible à la main.</p>
            <CheckField
              label="Archiver automatiquement les devis convertis ou refusés"
              checked={draft.wf_archive_quote}
              onChange={(next) => set("wf_archive_quote", next)}
            />
            <p className="fx-co-form__hint m-0">Le devis converti ou refusé sort de la liste Devis. Il reste sur la fiche client. La facture prend les conditions facture.</p>
          </div>
        </div>
        <div className="fx-fmt-row">
          <span>Récurrentes</span>
          <div className="flex flex-col gap-2">
            <CheckField
              label="Envoyer par e-mail lorsqu’elles sont validées"
              checked={draft.wf_email_recurring}
              onChange={(next) => set("wf_email_recurring", next)}
            />
            <p className="fx-co-form__hint m-0">
              L’autofacturation (Paiements → créer et envoyer le jour prévu) valide et envoie déjà. Cette case sert si
              vous validez un brouillon à la main.
            </p>
            <CheckField
              label="Arrêter si la dernière facture est impayée"
              checked={draft.wf_stop_unpaid}
              onChange={(next) => set("wf_stop_unpaid", next)}
            />
            <p className="fx-co-form__hint m-0">On ne crée plus la suivante tant que la dernière n’est pas payée.</p>
          </div>
        </div>
      </div>
      <p className="fx-co-form__hint">Une facture validée est verrouillée. Pas de déverrouillage, pas d’annulation : un avoir.</p>
      {err ? <p className="fx-co-form__err">{err}</p> : null}
      {msg ? <p className="fx-co-form__ok">{msg}</p> : null}
      <div className="fx-co-card__foot">
        <Button disabled={busy || !owner} onClick={() => void save()}>
          Enregistrer
        </Button>
      </div>
    </div>
  );
}
